/**
 * What a phone actually hands us when you pick a photo.
 *
 * The nutrition uploader used to validate with
 * `['image/jpeg','image/jpg','image/png','image/heic'].includes(file.type)`,
 * and that silently rejected real photos *before any request was made* — the
 * server never saw them, so the failure left no trace anywhere but an alert.
 *
 * Browsers do not agree on what a HEIC photo is:
 *
 *   iOS / Safari          image/heic
 *   newer iOS             image/heif  or  image/heic-sequence
 *   Chrome on Windows     ''          (empty string, very common for .heic)
 *   some sync clients     application/octet-stream
 *
 * Only the first of those passed. The equipment scanner had already been
 * widened to accept `image/heif` (VH-131); nutrition was never brought in line,
 * which is why photographing a machine worked while photographing a meal did not.
 *
 * So: trust the MIME type when it is one we recognise, and otherwise fall back
 * to the file extension. Being permissive here costs nothing — the server
 * re-validates, and converts HEIC through pillow-heif. Being strict cost every
 * HEIC upload from both a phone and a notebook.
 *
 * Deliberately NOT a general "is this an image" check: the extension list is the
 * same set the backend accepts, so the two ends cannot drift into a state where
 * the client uploads something the server will refuse.
 */

export const ACCEPTED_PHOTO_MIME = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]

export const ACCEPTED_PHOTO_EXT = /\.(jpe?g|png|heic|heif)$/i

/** The `accept` attribute. Extensions are listed alongside the MIME types
 *  because some pickers grey out .heic files when given MIME types alone. */
export const PHOTO_ACCEPT_ATTR =
  'image/jpeg,image/jpg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif'

/**
 * @param {{type?: string, name?: string}} file
 * @returns {boolean} whether the uploader should accept this file
 */
export const isAcceptedFoodPhoto = (file) => {
  if (!file) return false
  const mime = (file.type || '').toLowerCase()
  if (ACCEPTED_PHOTO_MIME.includes(mime)) return true
  return ACCEPTED_PHOTO_EXT.test(file.name || '')
}

export default isAcceptedFoodPhoto
